import authSession from "../lib/authSession";
import {
  Bell,
  User,
  BookOpen,
  Book,
  Compass,
  FileText,
  Layers,
  Newspaper,
  LayoutDashboard,
  Settings,
  LogOut,
  ShoppingCart,
  Receipt,
  Trophy,
  HelpCircle
} from "lucide-react";
import {Avatar, AvatarFallback, AvatarImage} from "../components/ui/avatar";
import {Badge} from "../components/ui/badge";
import {useCart} from "./CardContext";
import CartIcon from "./CartIcon";
import {useUserNotifications, useUserAuth} from "../store/user";

import React, {useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useEffect} from "react";
import {faSearch} from "@fortawesome/free-solid-svg-icons";
import {getLearnerAvatar, imageUrl} from "../lib/format";

const MenuSection = ({label, children}) => (
  <div className="border-b border-slate-100 px-1 py-1.5 last:border-b-0">
    <p className="m-0 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
    {children}
  </div>
);

const MenuItem = ({icon: Icon, to, label, badge, className = "", onClick = () => {}}) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => (to ? navigate(to) : onClick())}
      className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md hover:bg-secondary transition-colors text-sm ${className}`}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4" />
        <span>{label}</span>
      </div>
      {badge && <Badge className="h-5 px-2 bg-accent text-xs">{badge}</Badge>}
    </button>
  );
};

const NavBar = ({title, subTitle = "", search, setSearch, skipRemoteData = false}) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [lastName, setLastName] = useState("");
  const [uimage, setUimage] = useState("");
  const {cart} = useCart();
  // POST /api/auth/logout — revokes the session server-side before we clear it.
  const {userLogout} = useUserAuth();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    fetchUnreadCount,
    markNotificationRead,
    markAllNotificationsRead,
    notificationsLoading
  } = useUserNotifications();

  // The search box is optional: pages that pass `search`/`setSearch` get a
  // controlled box, the others keep the text locally (typing never crashes).
  const [localSearch, setLocalSearch] = useState("");
  const searchValue = search !== undefined ? search : localSearch;
  const handleSearchChange = (next) => {
    if (search === undefined) setLocalSearch(next);
    setSearch?.(next);
  };

  // Submit from the header search → keyword search on the course catalog.
  // Pages that bind `search`/`setSearch` filter live, so only navigate when the
  // box is uncontrolled (e.g. the landing page).
  const submitSearch = (event) => {
    event.preventDefault();
    if (search !== undefined) return;
    const term = String(searchValue || "").trim();
    navigate(term ? `/courses?search=${encodeURIComponent(term)}` : "/courses");
  };

  useEffect(() => {
    // `skipRemoteData` is set on public surfaces (e.g. the landing page) where
    // there may be no real session — skip the notification round-trips so they
    // never 401 into a global error toast.
    if (skipRemoteData) return undefined;
    fetchUnreadCount();
    fetchNotifications({ limit: 10 });
  }, [fetchUnreadCount, fetchNotifications, skipRemoteData]);

  useEffect(() => {
    const isAdmin = localStorage.getItem("isAdmin");
    if (isAdmin === "true") {
      window.location.href = `/login?next=${window.location.pathname}`;
      localStorage.setItem("error", "Login as a User");
      authSession.clearSession();
      return;
    }
    // The login flow stores a single `name` key (see persistUserSession); older
    // sessions used first_name/last_name — accept both.
    const sessionUser = authSession.getUser();
    const storedName =
      localStorage.getItem("name") ||
      [localStorage.getItem("first_name"), localStorage.getItem("last_name")].filter(Boolean).join(" ") ||
      sessionUser?.name || [sessionUser?.first_name, sessionUser?.last_name].filter(Boolean).join(" ");
    const [head, ...rest] = String(storedName || "").trim().split(/\s+/);
    setFirstName(head || "");
    setLastName(rest.join(" "));
    setUimage(getLearnerAvatar(sessionUser));
    setEmail(localStorage.getItem("uemail") || localStorage.getItem("email") || sessionUser?.email || "");
    const handleProfileUpdated = () => {
      const refreshedName = localStorage.getItem("name") || "";
      const [refreshedFirst, ...refreshedRest] = refreshedName.trim().split(/\s+/);
      setFirstName(refreshedFirst || "");
      setLastName(refreshedRest.join(" "));
      setUimage(getLearnerAvatar());
      setEmail(localStorage.getItem("uemail") || localStorage.getItem("email"));
    };
    window.addEventListener("fillop-profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("fillop-profile-updated", handleProfileUpdated);
  }, []);

  const handleLogout = async () => {
    // POST /api/auth/logout (best-effort) then drop the local session.
    await userLogout();
    localStorage.removeItem("isAdmin");
    // Return to the public landing page (logged-out marketing experience).
    window.location.href = "/";
  };

  // Profile menu is click-triggered and closes on outside click or Escape.
  const profileMenuRef = useRef(null);
  useEffect(() => {
    if (!showProfileMenu) return undefined;
    const onPointerDown = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) setShowProfileMenu(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setShowProfileMenu(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showProfileMenu]);

  return (
    <>
      {/* Navbar shell — plain Tailwind utilities + named theme tokens
          (bg-card, text-foreground, border-input, text-muted-foreground…). */}
      <div className="z-[1000] w-full bg-card shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="flex h-16 w-full items-center justify-between gap-4 px-4 sm:px-6">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <a href="/" aria-label="Fillop home" className="flex items-center">
              <img src="/images/logo.png" alt="Fillop" className="h-10 w-auto" />
            </a>
            <span className="font-inter text-2xl font-bold text-[#0e3b9a]">Fillop</span>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Search Bar */}
            <form
              onSubmit={submitSearch}
              role="search"
              className="relative hidden w-64 md:block lg:w-80"
            >
              <FontAwesomeIcon
                icon={faSearch}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Search..."
                aria-label="Search"
                className="w-full rounded-[100px] border border-input bg-card py-2 pl-9 pr-3 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-2 focus:ring-ring/30"
                value={searchValue}
                onChange={(event) => handleSearchChange(event.target.value)}
              />
            </form>
              {/* ShoppingCart */}
              <CartIcon />

              {/* Notifications */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowNotifications((v) => !v)}
                  className="relative rounded-full p-2 hover:bg-secondary transition-colors"
                  aria-label="Notifications"
                >
                  <Bell className="h-5 w-5 text-foreground" />
                  {unreadCount > 0 && (
                    <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-accent text-xs">
                      {unreadCount}
                    </Badge>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 z-[999999] mt-1 w-80 rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
                    <div className="flex items-center justify-between border-b border-border px-4 py-2">
                      <p className="m-0 text-sm font-semibold">Notifications</p>
                      <button
                        type="button"
                        onClick={() => markAllNotificationsRead()}
                        className="text-xs font-medium text-accent hover:underline"
                      >
                        Mark all read
                      </button>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notificationsLoading && notifications.length === 0 ? (
                        <p className="m-0 px-4 py-3 text-sm text-muted-foreground">Loading…</p>
                      ) : notifications.length === 0 ? (
                        <p className="m-0 px-4 py-3 text-sm text-muted-foreground">No notifications.</p>
                      ) : (
                        notifications.map((n) => (
                          <button
                            key={n.id}
                            type="button"
                            onClick={() => markNotificationRead(n.id)}
                            className={`block w-full px-4 py-3 text-left text-sm hover:bg-secondary ${
                              n.read || n.status === "read" ? "opacity-60" : "font-medium"
                            }`}
                          >
                            <span className="block text-foreground">
                              {n.title || n.message || "Notification"}
                            </span>
                            {n.message && n.title && (
                              <span className="block text-xs text-muted-foreground">{n.message}</span>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Profile Dropdown */}
              <div
                ref={profileMenuRef}
                className="relative p-[20px] m-[-20px]"
              >
                <button
                  type="button"
                  aria-label="Account menu"
                  aria-haspopup="true"
                  aria-expanded={showProfileMenu}
                  onClick={() => setShowProfileMenu((open) => !open)}
                  className="flex items-center gap-2 rounded-full transition-opacity hover:opacity-80"
                >
                  <Avatar className="h-12 w-12 border-2 border-primary/20">
                    <AvatarImage src={imageUrl(uimage) || "/images/default_profile.png"} alt="Profile" />
                    <AvatarFallback className="bg-slate-100 text-sm font-semibold text-slate-600">
                      {(firstName?.[0] || "L").toUpperCase()}
                      {(lastName?.[0] || "").toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>

                {/* Dropdown Menu — always mounted so it animates both open & close */}
                <div
                  aria-hidden={!showProfileMenu}
                  className={`absolute right-0 z-[999999] mt-1 w-72 origin-top-right overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl transition-all duration-200 ease-out ${
                    showProfileMenu
                      ? "translate-y-0 scale-100 opacity-100"
                      : "pointer-events-none -translate-y-1 scale-95 opacity-0"
                  }`}
                  onClick={() => setShowProfileMenu(false)}
                >
                    <div className="border-b border-border p-4">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-12 w-12">
                          <AvatarImage src={imageUrl(uimage) || "/images/default_profile.png"} alt="Profile" />
                          <AvatarFallback className="bg-slate-100 text-sm font-semibold text-slate-600">
                            {(firstName?.[0] || "L").toUpperCase()}
                            {(lastName?.[0] || "").toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">
                            {[firstName, lastName].filter(Boolean).join(" ") || email || "Learner"}
                          </p>
                          <p className="truncate text-xs text-muted-foreground">{email || ""}</p>
                        </div>
                      </div>
                    </div>

                    <div className="max-h-[70vh] overflow-y-auto">
                      <MenuSection label="Learning">
                        <MenuItem icon={LayoutDashboard} to="/" label="Home" />
                        {/* GET /api/learners/courses/enrolled */}
                        <MenuItem icon={BookOpen} to="/my-courses" label="My Courses" />
                        {/* GET /api/learners/library */}
                        <MenuItem icon={Layers} to="/content-library" label="My Library" />
                        {/* GET /api/achievements */}
                        <MenuItem icon={Trophy} to="/achievements" label="Achievements" />
                      </MenuSection>

                      <MenuSection label="Catalog">
                        {/* GET /api/learners/courses — browse + filter by category */}
                        <MenuItem icon={Compass} to="/courses" label="All Courses" />
                        {/* GET /api/learners/books */}
                        <MenuItem icon={Book} to="/books" label="Books" />
                        {/* GET /api/learners/journals */}
                        <MenuItem icon={FileText} to="/journals" label="Journals" />
                        {/* GET /api/learners/dashboard/recent-resources */}
                        <MenuItem icon={Layers} to="/learner/resources" label="Recent Resources" />
                      </MenuSection>

                      <MenuSection label="Updates">
                        {/* GET /api/learners/dashboard/news */}
                        <MenuItem icon={Newspaper} to="/learner/news" label="News & Announcements" />
                        {/* GET /api/learners/notifications* */}
                        <MenuItem
                          icon={Bell}
                          label="Notifications"
                          badge={unreadCount > 0 ? unreadCount : undefined}
                          onClick={() => setShowNotifications(true)}
                        />
                      </MenuSection>

                      <MenuSection label="Purchases">
                        <MenuItem
                          icon={ShoppingCart}
                          to="/cart"
                          label="Cart"
                          badge={cart.length > 0 ? cart.length : undefined}
                        />
                        {/* GET /api/learners/purchases */}
                        <MenuItem icon={Receipt} to="/learner/transactions" label="Purchase History" />
                      </MenuSection>

                      <MenuSection label="Account">
                        {/* GET/PATCH /api/learners/me */}
                        <MenuItem icon={User} to={"/account/settings"} label="My Profile" />
                        {/* POST /api/auth/change-password */}
                        <MenuItem
                          icon={Settings}
                          to={"/account/settings?tab=security"}
                          label="Account Settings"
                        />
                        <MenuItem icon={HelpCircle} to="/contact" label="FAQ & Support" />
                      </MenuSection>
                    </div>

                    <div className="border-t border-border p-2">
                      <MenuItem
                        icon={LogOut}
                        onClick={handleLogout}
                        label="Logout"
                        className="text-destructive hover:bg-destructive/10"
                      />
                    </div>
                  </div>
              </div>
            </div>
          </div>
        {title ? (
          <div className="flex flex-wrap items-center gap-1 px-4 pb-3 sm:px-6">
            <h3 className="m-0 text-xl font-bold leading-tight text-foreground">{title}</h3>
            {subTitle !== "" ? (
              <h5 className="m-0 text-[15px] leading-tight text-muted-foreground">/{subTitle}</h5>
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default NavBar;
